import {
  CheckCircle2,
  Code2,
  Loader2,
  Plus,
  Search,
  X,
  XCircle,
} from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import type { TestTemplate } from '../../shared/hooks/use-test-templates'
import { useTestTemplates } from '../../shared/hooks/use-test-templates'

// Simplified template type for selection
export interface TemplateForSelection {
  id: string
  name: string
  slug: string
  description: string | null
}

type ModalMode = 'select' | 'create'

interface AddTemplateModalProps {
  isOpen: boolean
  onClose: () => void
  onAdd: (templates: TemplateForSelection[]) => void
  excludeIds?: string[] // Templates already added to the problem
}

export function AddTemplateModal({
  isOpen,
  onClose,
  onAdd,
  excludeIds = [],
}: AddTemplateModalProps) {
  const { templates, loading, error, refresh, createTemplate } =
    useTestTemplates()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [mode, setMode] = useState<ModalMode>('select')

  // Create form state
  const [newName, setNewName] = useState('')
  const [newDescription, setNewDescription] = useState('')
  const [newCode, setNewCode] = useState('')
  const [creating, setCreating] = useState(false)

  // Reset selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set())
      setSearchQuery('')
      setMode('select')
      setNewName('')
      setNewDescription('')
      setNewCode('')
    }
  }, [isOpen])

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  // Filter out already-added templates and apply search
  const availableTemplates = useMemo(() => {
    const excludeSet = new Set(excludeIds)
    return templates
      .filter((t) => !excludeSet.has(t.id))
      .filter((t) => {
        if (!searchQuery.trim()) return true
        const query = searchQuery.toLowerCase()
        return (
          t.name.toLowerCase().includes(query) ||
          t.description?.toLowerCase().includes(query) ||
          t.slug.toLowerCase().includes(query)
        )
      })
  }, [templates, excludeIds, searchQuery])

  const toggleSelection = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const handleAdd = () => {
    if (selectedIds.size === 0) return
    // Get full template objects for selected IDs
    const selectedTemplates = templates
      .filter((t) => selectedIds.has(t.id))
      .map((t) => ({
        id: t.id,
        name: t.name,
        slug: t.slug,
        description: t.description,
      }))
    onAdd(selectedTemplates)
    onClose()
  }

  const handleCreateTemplate = async () => {
    if (!newName.trim()) {
      toast.error('Template name is required')
      return
    }

    setCreating(true)
    try {
      const newTemplate = await createTemplate({
        name: newName.trim(),
        description: newDescription.trim() || null,
        code: newCode.trim()
          ? [
              {
                language: 'python',
                definitionCode: newCode.trim(),
                testCode: '',
                testExpectedOutput: '',
              },
            ]
          : undefined,
      })

      toast.success(`Template "${newTemplate.name}" created`)

      // Add the newly created template to the problem
      onAdd([
        {
          id: newTemplate.id,
          name: newTemplate.name,
          slug: newTemplate.slug,
          description: newTemplate.description,
        },
      ])
      onClose()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create template',
      )
    } finally {
      setCreating(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className="relative z-10 w-full max-w-lg max-h-[80vh] flex flex-col rounded-lg border border-border bg-card shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border p-4">
          <h2 className="text-lg font-semibold text-foreground">
            {mode === 'select' ? 'Add Templates' : 'Create New Template'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          <button
            type="button"
            onClick={() => setMode('select')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'select'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Select Existing
          </button>
          <button
            type="button"
            onClick={() => setMode('create')}
            className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'create'
                ? 'border-b-2 border-primary text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Plus size={14} className="inline mr-1" />
            Create New
          </button>
        </div>

        {mode === 'select' ? (
          <>
            {/* Search */}
            <div className="border-b border-border p-4">
              <div className="relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search templates..."
                  className="pl-9"
                />
              </div>
            </div>

            {/* Template List */}
            <div className="flex-1 overflow-y-auto p-2">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2
                    size={24}
                    className="animate-spin text-muted-foreground"
                  />
                </div>
              ) : error ? (
                <div className="py-8 text-center">
                  <p className="mb-2 text-sm text-muted-foreground">{error}</p>
                  <Button variant="outline" size="sm" onClick={refresh}>
                    Retry
                  </Button>
                </div>
              ) : availableTemplates.length === 0 ? (
                <div className="py-8 text-center">
                  <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <Code2 size={20} className="text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {templates.length === 0
                      ? 'No templates available. Create one using the "Create New" tab.'
                      : searchQuery
                        ? 'No templates match your search.'
                        : 'All templates have already been added.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-1">
                  {availableTemplates.map((template) => (
                    <TemplateOption
                      key={template.id}
                      template={template}
                      isSelected={selectedIds.has(template.id)}
                      onToggle={() => toggleSelection(template.id)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between border-t border-border p-4">
              <span className="text-sm text-muted-foreground">
                {selectedIds.size} selected
              </span>
              <div className="flex gap-2">
                <Button type="button" variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={handleAdd} disabled={selectedIds.size === 0}>
                  Add {selectedIds.size > 0 ? `(${selectedIds.size})` : ''}
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Create Form */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Name <span className="text-destructive">*</span>
                </label>
                <Input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g., MessageNode, ExpressionNode"
                  disabled={creating}
                />
                <p className="text-xs text-muted-foreground">
                  A unique name for this template (slug is auto-generated)
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Description
                </label>
                <Input
                  type="text"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="e.g., Tree node with text field for hidden message problem"
                  disabled={creating}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">
                  Python Code
                </label>
                <Textarea
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  placeholder={`class MyNode:\n    def __init__(self, val, left=None, right=None):\n        self.val = val\n        self.left = left\n        self.right = right`}
                  className="font-mono text-sm min-h-[120px]"
                  disabled={creating}
                />
                <p className="text-xs text-muted-foreground">
                  Class definition that will be included in the test harness.
                  You can add test code and validation later.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end border-t border-border p-4 gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateTemplate}
                disabled={creating || !newName.trim()}
              >
                {creating ? (
                  <>
                    <Loader2 size={14} className="animate-spin mr-2" />
                    Creating...
                  </>
                ) : (
                  'Create & Add'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

interface TemplateOptionProps {
  template: TestTemplate
  isSelected: boolean
  onToggle: () => void
}

function TemplateOption({
  template,
  isSelected,
  onToggle,
}: TemplateOptionProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`w-full rounded-lg p-3 text-left transition-colors ${
        isSelected
          ? 'bg-primary/10 border border-primary/50'
          : 'hover:bg-muted/50 border border-transparent'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox indicator */}
        <div
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border ${
            isSelected
              ? 'bg-primary border-primary text-primary-foreground'
              : 'border-muted-foreground/50'
          }`}
        >
          {isSelected && <CheckCircle2 size={14} />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-foreground">{template.name}</span>
            {/* Language validation indicators */}
            <div className="flex items-center gap-1">
              {template.languageSummary.python !== undefined && (
                <span
                  className="flex items-center gap-0.5 text-xs"
                  title={
                    template.languageSummary.python
                      ? 'Python validated'
                      : 'Python not validated'
                  }
                >
                  {template.languageSummary.python ? (
                    <CheckCircle2 className="h-3 w-3 text-green-500" />
                  ) : (
                    <XCircle className="h-3 w-3 text-muted-foreground" />
                  )}
                  <span className="text-muted-foreground">py</span>
                </span>
              )}
            </div>
          </div>
          {template.description && (
            <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">
              {template.description}
            </p>
          )}
        </div>
      </div>
    </button>
  )
}
